// Das K2-PDF am Gerät prüfen, bevor es abgelegt wird (E2, Schritt 3; Plan
// E2: „Das PDF aus <input> wird als ArrayBuffer kopiert“). Das File-Objekt
// aus dem <input> zeigt am iPhone nur auf die Datei – liegt sie nicht mehr
// dort (iCloud, verschoben), wäre das PDF beim Senden nicht mehr lesbar.
// Deshalb kommt eine Kopie der Bytes in den Speicher am Gerät.
//
// Geprüft wird mit derselben Regel wie am Server (Kern,
// `dateiInhaltPruefen`): höchstens 25 MB, und die Datei beginnt mit
// „%PDF-“. So erfährt der Verkäufer gleich beim Ablegen, wenn er das
// Falsche gewählt hat – nicht erst Stunden später als Fehler beim Senden.
//
// Seit E3c (S5; ANFORDERUNGEN.md H24) ebenso der Excel-Report von K2: nur
// .xlsx, höchstens 10 MB, ganz geprüft (das Inhaltsverzeichnis einer .xlsx
// steht am Ende) – und „Dachplanung starten“ öffnet K2 unter der Adresse,
// die die Leitung eingetragen hat (nur http und https).

import { ANFANG_ZUM_ERKENNEN, DATEI_ARTEN, dateiInhaltPruefen, XLSX } from "@happy-energy/kern";
import type { PdfBereit } from "./arbeitskopie.ts";
import { pruefsummeBerechnen } from "./foto.ts";

/** Das größte PDF, das der Server annimmt. */
export const PDF_HOECHSTENS = DATEI_ARTEN["application/pdf"];
/** Der größte Excel-Report, den der Server annimmt. */
export const EXCEL_HOECHSTENS = DATEI_ARTEN[XLSX];

/** „1,2 MB“, „380 KB“ – für Menschen, mit Komma. */
export function groesseZeigen(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toLocaleString("de-AT", { maximumFractionDigits: 1 })} MB`;
}

export type PdfPruefung = { ok: true; pdf: PdfBereit } | { ok: false; meldung: string };

/** Vom gewählten PDF zur Kopie am Gerät: Größe und Anfang prüfen, Bytes kopieren, Prüfsumme. */
export async function pdfVorbereiten(datei: File): Promise<PdfPruefung> {
  if (datei.size > PDF_HOECHSTENS) {
    return { ok: false, meldung: `Das PDF ist zu groß (${groesseZeigen(datei.size)}) – höchstens ${groesseZeigen(PDF_HOECHSTENS)}.` };
  }
  const daten = await datei.arrayBuffer();
  const anfang = new Uint8Array(daten, 0, Math.min(ANFANG_ZUM_ERKENNEN, daten.byteLength));
  if (dateiInhaltPruefen("application/pdf", anfang) !== null) {
    return { ok: false, meldung: "Das ist kein PDF. Bitte das PDF der Dachplanung aus K2 wählen." };
  }
  return { ok: true, pdf: { daten, dateiname: datei.name || "Dachplanung.pdf", pruefsumme: await pruefsummeBerechnen(daten) } };
}

/** Vom gewählten Excel-Report zur Kopie am Gerät (seit E3c, S5): Größe und Inhalt prüfen, Bytes kopieren, Prüfsumme. */
export async function excelVorbereiten(datei: File): Promise<PdfPruefung> {
  if (datei.size > EXCEL_HOECHSTENS) {
    return { ok: false, meldung: `Die Liste ist zu groß (${groesseZeigen(datei.size)}) – höchstens ${groesseZeigen(EXCEL_HOECHSTENS)}.` };
  }
  const daten = await datei.arrayBuffer();
  if (dateiInhaltPruefen(XLSX, new Uint8Array(daten)) !== null) {
    return { ok: false, meldung: "Das ist kein Excel-Report (.xlsx). Bitte die Liste aus K2 als Excel (.xlsx) wählen." };
  }
  return { ok: true, pdf: { daten, dateiname: datei.name || "K2-Liste.xlsx", pruefsumme: await pruefsummeBerechnen(daten) } };
}

/**
 * Die Adresse von K2 zum Öffnen – nur http und https (die Leitung trägt sie
 * ein, der Server prüft sie schon; hier noch einmal, weil sie als Link in
 * einem neuen Tab aufgeht). null: keine brauchbare.
 */
export function k2AdresseZumOeffnen(adresse: string | null | undefined): string | null {
  if (!adresse) return null;
  try {
    const url = new URL(adresse);
    return url.protocol === "https:" || url.protocol === "http:" ? url.href : null;
  } catch {
    return null;
  }
}
